import { useState, useCallback } from 'react';
import { Card, Button, ProgressInfo, Skeleton, useSpinner, useToast } from '@chikara/ui';
import { formatMXN } from '@/features/dashboard/utils/dashboard.utils';
import type { DashboardSummaryProps } from '@/features/dashboard/types/dashboard.types';
import { MakePaymentModal } from '@/features/credits/components/MakePaymentModal';
import { hasPermission } from '@/shared/config/permissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import styles from './DashboardSummary.module.css';
import { t } from '@/i18n';



export function DashboardSummary({ summary, userSpending, linkedAccount }: DashboardSummaryProps) {
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const { userData } = useAuth();
  const { isSpinnerLoading } = useSpinner();
  const { info } = useToast();

  const handleMakePayment = useCallback(() => {
    // TODO: setIsPaymentModalOpen(true);
    info(t('common.featureComingSoon'));
  }, [info]);

  const handleClosePaymentModal = useCallback(() => {
    if (!isConfirmingPayment) {
      setIsPaymentModalOpen(false);
    }
  }, [isConfirmingPayment]);

  const handleConfirmPayment = useCallback(async () => {
    setIsConfirmingPayment(true);
    // TODO: llamar al servicio de pago
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsConfirmingPayment(false);
    setIsPaymentModalOpen(false);
  }, []);


  return (
    <>
      {/* Sección de Crédito de tu cuenta */}
      {hasPermission(userData?.role, 'VIEW_DASHBOARD_SUMMARY') && (
        <section className={styles.section}>
          <h2 className={styles.subsectionTitle}>{t('dashboard.creditSectionTitle')}</h2>

          <div className={styles.creditGrid}>
            {isSpinnerLoading ? (
              <>
                <Card className={styles.creditInfoField}>
                  <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                    <Skeleton width="100px" height="14px" />
                    <Skeleton width="150px" height="24px" />
                  </div>
                </Card>
                <Card className={styles.creditInfoField} style={{ flex: 2 }}>
                  <div style={{ padding: '1rem', display: 'flex', justifyContent: 'space-around', gap: '1rem', width: '100%' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                      <Skeleton width="80px" height="14px" />
                      <Skeleton width="120px" height="24px" />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                      <Skeleton width="80px" height="14px" />
                      <Skeleton width="120px" height="24px" />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                      <Skeleton width="80px" height="14px" />
                      <Skeleton width="120px" height="24px" />
                    </div>
                  </div>
                </Card>
              </>
            ) : (
              <>
                <Card className={styles.creditInfoField}>
                  <Card.Stats
                    label={t('dashboard.creditLineLabel')}
                    value={formatMXN(summary?.creditLine || 0)}
                    align="center"
                  />
                </Card>
                
                <Card orientation="horizontal" className={styles.creditInfoField}>
                  <Card.Stats
                    label={t('dashboard.spentLabel')}
                    value={formatMXN(summary?.spent || 0)}
                    align="center"
                  />

                  <Card.Stats
                    label={t('dashboard.disposableLabel')}
                    value={formatMXN(summary?.disposable || 0)}
                    align="center"
                  />

                  <Card.Stats
                    label={
                      <span className={styles.paymentLabel}>
                        <span>{t('dashboard.toPayLabel')} </span>
                        <span className={styles.paymentDate}>{summary?.paymentDate}</span>
                      </span>
                    }
                    value={formatMXN(summary?.toPay || 0)}
                    action={
                      <Button
                        variant="link-tertiary"
                        size="md"
                        onClick={handleMakePayment}
                      >
                        {t('dashboard.payAction')}
                      </Button>
                    }
                  />                   
                </Card>
              </>
            )}
          </div>
        </section>
      )}

      {/* Sección de Tu gasto como usuario */}
      <section className={styles.section}>
        <h2 className={styles.subsectionTitle}>{t('dashboard.userSpendingTitle')}</h2>
        <div className={styles.userSpendingContainer}>
          {isSpinnerLoading ? (
             <>
              <Card className={`${styles.creditInfoField} ${styles.limitCard}`}>
                 <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                    <Skeleton width="80px" height="14px" />
                    <Skeleton width="120px" height="24px" />
                  </div>
              </Card>
              <Card className={`${styles.progressCard} ${styles.creditInfoField}`}>
                 <div style={{ padding: '1rem', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                    <Skeleton width="120px" height="14px" />
                    <Skeleton width="100%" height="12px" />
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Skeleton width="60px" height="14px" />
                      <Skeleton width="60px" height="14px" />
                    </div>
                  </div>
              </Card>
             </>
          ) : (
            <>
              <Card className={`${styles.creditInfoField} ${styles.limitCard}`}>
                <Card.Stats
                  label={t('dashboard.userLimitLabel')}
                  value={formatMXN(userSpending?.limit || 0)}
                  align="center"
                />
              </Card>
              
              <Card className={`${styles.progressCard} ${styles.creditInfoField}`}>
                <Card.Stats
                  label={t('dashboard.userSpentLabel')}
                  align="center"
                  className={styles.progressStats}
                  value={
                    <div style={{ width: '100%' }}>
                        <ProgressInfo
                          total={userSpending?.limit || 0}
                          spent={userSpending?.spent || 0}
                          formatValue={formatMXN}
                        />
                    </div>
                  }
                />
              </Card>
            </>
          )}
        </div>

        <MakePaymentModal
          isOpen={isPaymentModalOpen}
          onClose={handleClosePaymentModal}
          amount={summary?.toPay || 0}
          linkedAccount={linkedAccount}
          onConfirm={handleConfirmPayment}
          isConfirming={isConfirmingPayment}
        />
      </section>
    </>
  );
}
